// frontend/packages/business-console/src/pages/StaffLanding.jsx
//
// Landing page for internal UpRyzze staff with no client selected. The
// ClientSwitcher in the topbar is the primary affordance — this page
// just gives the operator a friendly empty state and a recent-activity
// summary pulled from their own audit log.

import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";

import { useAuth } from "../contexts/AuthContext";
import { api } from "@upryzze/services";
import ClientSwitcher from "../components/staff/ClientSwitcher";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function StaffLanding() {
  const { user, isStaff } = useAuth();
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isStaff) {
      setLoading(false);
      return;
    }
    let alive = true;
    api
      .get("/staff/audit-logs?limit=20", { headers: { "x-business-id": "" } })
      .then((res) => {
        if (!alive) return;
        setRecent(Array.isArray(res?.data?.logs) ? res.data.logs : []);
      })
      .catch(() => {
        if (!alive) return;
        setRecent([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [isStaff]);

  if (!isStaff) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5">Access required</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This page is for internal UpRyzze staff.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Pick a client
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        You&apos;re signed in as <strong>{user?.email}</strong>. Choose a
        client to scope into their Console.
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <ClientSwitcher />
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Your recent activity
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : recent.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No recent client access.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {recent.map((row) => (
            <Card key={row._id} variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip
                    label={row.method}
                    size="small"
                    color={row.statusCode >= 400 ? "error" : "default"}
                    sx={{ fontWeight: 600, minWidth: 60 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }} noWrap>
                      {row.path}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(row.createdAt)} · biz {String(row.targetBusinessId).slice(-6)} ·{" "}
                      {row.statusCode} · {row.durationMs}ms
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
